import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import SingleProductDetails from '../MerchantDetails/SingleProductDetails';
import useCartActions from '../../hooks/useCartActions';
import Toast from '../Toast';
import { useUser } from '../../contexts/user';
import { isMerchantServiceable } from '../../utils/serviceability';
import { SUPPORT_EMAIL } from '../../config/appConfig';

const FeaturedProducts = ({ groups }) => {
  const { t, i18n } = useTranslation();
  const { user } = useUser();
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [variantDecision, setVariantDecision] = useState(null);

  const { getMaxVariantFromCart, getVariantQuantitiesFromCart, changeQuantity, toastMessage, toastType } = useCartActions();

  // Flatten every merchant's featured products into a single de-duplicated
  // list — no per-merchant header, no category grouping. Order preserved
  // from the API response. Tag each product with its merchant + computed
  // serviceability so out-of-zone cards can show a "request delivery"
  // notice instead of being filtered out.
  const allFeaturedProducts = (() => {
    const seen = new Set();
    const out = [];
    (groups || []).forEach((merchant) => {
      const serviceable = isMerchantServiceable(merchant, user);
      (merchant?.featuredProducts || []).forEach((product) => {
        const id = product?._id;
        if (!id || seen.has(String(id))) {
          return;
        }
        seen.add(String(id));
        out.push({ product, merchant, serviceable });
      });
    });
    return out;
  })();

  const buildMailtoForMerchant = (merchant) => {
    const merchantName = merchant?.storeName || '';
    const subject = encodeURIComponent(
      t('outOfZoneEmailSubject', { merchant: merchantName }),
    );
    const body = encodeURIComponent(
      t('outOfZoneEmailBody', { merchant: merchantName }),
    );
    return `mailto:${SUPPORT_EMAIL}?subject=${subject}&body=${body}`;
  };

  const handleVariantQuantityIntent = (product, type, merchant) => {
    const { item } = getMaxVariantFromCart(product._id);
    if (!product.variants?.length) {
      changeQuantity(product, type);
      return;
    }
    setVariantDecision({ product, merchant, variantItem: item, type });
  };

  const handleModifySameVariant = () => {
    if (!variantDecision) { return; }
    const { product, variantItem, type } = variantDecision;
    changeQuantity({ ...product, variantId: variantItem.variantId }, type);
    setVariantDecision(null);
  };

  const handleChooseAnotherVariant = () => {
    if (!variantDecision) { return; }
    setSelectedProduct({
      product: variantDecision.product,
      merchant: variantDecision.merchant,
    });
    setVariantDecision(null);
  };

  return (
    <>
      <section className="pt-6 px-2 md:px-4 lg:px-6">
        <h2 className='mb-3 text-xl font-semibold text-[#000000DE]'>{t('Featured products')}</h2>

        {allFeaturedProducts.length > 0 && (
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 2xl:grid-cols-8 gap-3">
            {allFeaturedProducts.map(({ product, merchant, serviceable }) => {
              const productName = i18n.language.startsWith('fr')
                ? product.frName
                : product.enName;
              const hasPrice =
                product.price !== null && product.price !== undefined;
              const cartQty = getMaxVariantFromCart(product._id).quantity;
              return (
                <div
                  key={product._id}
                  className={`text-black border border-gray-300 rounded-xl bg-white overflow-hidden flex flex-col cursor-pointer transition-all duration-300 ease-out hover:border-[#dddddd] hover:shadow-md ${
                    !serviceable ? 'opacity-90' : ''
                  }`}
                  role="button"
                  tabIndex={0}
                  onClick={() => setSelectedProduct({ product, merchant })}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      setSelectedProduct({ product, merchant });
                    }
                  }}
                >
                  <div className="w-full relative bg-white rounded-lg overflow-hidden flex items-center justify-center aspect-[3/4]">
                    {product.images?.[0] ? (
                      <img
                        src={product.images[0]}
                        alt={productName || ''}
                        loading="lazy"
                        className={`w-full h-full object-contain transition-all duration-300 ${
                          product.availability !== 'in-stock' || !serviceable
                            ? 'filter grayscale brightness-90'
                            : ''
                        }`}
                      />
                    ) : (
                      <span className="text-gray-400 text-xs">
                        {t('No image')}
                      </span>
                    )}
                    {product.availability !== 'in-stock' && (
                      <div className="absolute top-0 left-0 w-full bg-black bg-opacity-50 py-0.5 px-1 flex items-center justify-center rounded-t-lg">
                        <span className="text-white text-xs font-semibold text-center">
                          {t('Out of stock')}
                        </span>
                      </div>
                    )}
                    {!serviceable && product.availability === 'in-stock' && (
                      <span className="bg-[#b54708] text-white py-0.5 px-1.5 text-[10px] leading-4 rounded-br absolute left-0 top-0 z-10">
                        {t('outOfZoneBadge')}
                      </span>
                    )}
                  </div>

                  <div className="p-2 flex flex-col flex-1">
                    <h3 className="text-xs sm:text-sm font-semibold mb-1 line-clamp-2">
                      {productName}
                    </h3>

                    {!serviceable && (
                      <a
                        href={buildMailtoForMerchant(merchant)}
                        onClick={(e) => e.stopPropagation()}
                        className="mt-auto inline-block text-[10px] sm:text-xs font-medium text-center px-2 py-1.5 rounded border border-[#0b1223] text-[#0b1223] hover:bg-[#0b1223] hover:text-white transition-colors"
                      >
                        {t('contactNeobox')}
                      </a>
                    )}

                    {serviceable && product.availability === 'in-stock' && (
                      <div className="flex items-center justify-between font-semibold mt-1 gap-x-1 flex-wrap">
                        {hasPrice ? (
                          <span className="text-xs sm:text-sm">
                            {product.price}$
                          </span>
                        ) : (
                          <p className="text-[10px] text-[#999] leading-tight line-clamp-2 flex-1">
                            {t('noPriceMessage')}
                          </p>
                        )}

                        {hasPrice && (
                          <div className="text-xs rounded border border-[#E1E1E1] text-center leading-tight whitespace-normal">
                            {cartQty > 0 ? (
                              <div className="flex items-center text-sm font-medium bg-black text-white">
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleVariantQuantityIntent(
                                      product,
                                      'decrease',
                                      merchant,
                                    );
                                  }}
                                  className="px-2 text-base"
                                >
                                  −
                                </button>
                                <span className="px-1.5">{cartQty}</span>
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleVariantQuantityIntent(
                                      product,
                                      'increase',
                                      merchant,
                                    );
                                  }}
                                  className="px-2 text-base"
                                >
                                  +
                                </button>
                              </div>
                            ) : (
                              <button
                                className="px-2 py-1"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setSelectedProduct({ product, merchant });
                                }}
                              >
                                {t('learnMore')}
                              </button>
                            )}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        <Toast message={toastMessage} type={toastType} />

        {variantDecision && (
          <div
            className="fixed inset-0 bg-black/40 flex items-end justify-center z-50"
            onClick={() => setVariantDecision(null)}
          >
            <div
              className="
                      bg-white w-full max-w-lg
                      p-6
                      shadow-lg
                      animate-slide-up2
                    "
              onClick={(e) => e.stopPropagation()}
            >
              <div className="-mx-6 -mt-6 mb-4 bg-[#ccc] px-6 py-4">
                <h3 className="text-lg font-semibold leading-tight">
                  {i18n.language.startsWith('fr')
                    ? variantDecision.product?.frName
                    : variantDecision.product?.enName}
                </h3>
              </div>

              <p className="text-sm mb-6">
                {t('previousSelections')}: {i18n.language.startsWith('fr')
                  ? variantDecision.variantItem?.frVariantName
                  : variantDecision.variantItem?.enVariantName}
              </p>

              <div className="flex gap-3">
                <button
                  onClick={handleChooseAnotherVariant}
                  className="flex-1 border border-black bg-white py-3 font-medium hover:bg-gray-100"
                >
                  {t('chooseVariant')}
                </button>

                <button
                  onClick={handleModifySameVariant}
                  className="flex-1 bg-black text-white border border-gray-300 py-3 font-medium"
                >
                  {t('repeat')}
                </button>
              </div>
            </div>
          </div>
        )}

        {selectedProduct && (
          <SingleProductDetails
            product={selectedProduct.product}
            merchant={selectedProduct.merchant}
            cartQuantities={getVariantQuantitiesFromCart(selectedProduct.product._id)}
            onClose={() => setSelectedProduct(null)}
          />
        )}
      </section>
    </>
  );
};

export default FeaturedProducts;
